import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import api, { setIframeMode } from './api';
import { detectIframe, isAllowedOrigin } from './iframeUtils';
import { usePostMessage } from './PostMessageContext';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const { sendToParent } = usePostMessage();
  const [user, setUser] = useState(() => {
    try {
      const u = localStorage.getItem('qp_user');
      return u ? JSON.parse(u) : null;
    } catch { return null; }
  });
  const [brand, setBrand] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isIframe, setIsIframe] = useState(false);
  const [iframePending, setIframePending] = useState(() => detectIframe());
  const iframeBrandReceived = useRef(false);
  const getBrandSent = useRef(false);

  // Persistent listener for brand messages from parent
  useEffect(() => {
    if (!detectIframe()) return;

    const handleMessage = (event) => {
      if (!isAllowedOrigin(event.origin)) return;
      if (event.data && event.data.type === 'brand' && !iframeBrandReceived.current) {
        iframeBrandReceived.current = true;
        const iframeBrand = event.data.payload || event.data.brand || event.data;
        const brandData = { ...iframeBrand };
        delete brandData.type;
        setIsIframe(true);
        setIframePending(false);
        setIframeMode(true);
        setBrand(brandData);
        setUser({ name: brandData.title || 'Embedded User', email: brandData.email || '' });
      }
    };

    window.addEventListener('message', handleMessage);

    if (!getBrandSent.current) {
      getBrandSent.current = true;
      sendToParent({ type: 'get_brand' });
    }

    // Fallback: stop showing loading screen after 5s even if no brand arrives
    const timeout = setTimeout(() => {
      if (!iframeBrandReceived.current) setIframePending(false);
    }, 5000);

    return () => {
      window.removeEventListener('message', handleMessage);
      clearTimeout(timeout);
    };
  }, []);

  const login = async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    const { token, user: u, brand: b } = res.data;
    localStorage.setItem('qp_token', token);
    localStorage.setItem('qp_user', JSON.stringify(u));
    localStorage.setItem('qp_brand', JSON.stringify(b));
    setUser(u);
    setBrand(b);
    return res.data;
  };

  const logout = () => {
    if (isIframe) return;
    localStorage.removeItem('qp_token');
    localStorage.removeItem('qp_user');
    localStorage.removeItem('qp_brand');
    setUser(null);
    setBrand(null);
    window.location.href = '/login';
  };

  const refreshUser = async () => {
    try {
      const res = await api.get('/auth/me');
      setUser(res.data.user);
      setBrand(res.data.brand);
      localStorage.setItem('qp_user', JSON.stringify(res.data.user));
      localStorage.setItem('qp_brand', JSON.stringify(res.data.brand));
    } catch (e) {
      // ignore
    }
  };

  return (
    <AuthContext.Provider value={{ user, brand, setBrand, login, logout, loading, refreshUser, isIframe, iframePending }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
